import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  Settings, Plus, Trash2, Save, GripVertical, ChevronUp, ChevronDown,
  FileText, CheckCircle, XCircle, Edit2, X, Building2, ListChecks
} from 'lucide-react';
import { Link } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import api from '../services/api';

const BRAND_BLUE = '#2B4B6A';

const AdminCompanyApprovalSteps = () => {
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingStep, setEditingStep] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newStep, setNewStep] = useState({
    name: '',
    description: '',
    order_number: 1,
    requires_documents: false,
    is_active: true
  });

  useEffect(() => {
    fetchSteps();
  }, []);

  const fetchSteps = async () => {
    try {
      const res = await api.get('/admin/company-approval/steps');
      setSteps(res.data);
    } catch (error) {
      toast.error('Failed to load approval steps');
    } finally {
      setLoading(false);
    }
  };

  const handleAddStep = async () => {
    if (!newStep.name.trim()) {
      toast.error('Step name is required');
      return;
    }

    setSaving(true);
    try {
      const maxOrder = steps.length > 0 ? Math.max(...steps.map(s => s.order_number)) : 0;
      await api.post('/admin/company-approval/steps', {
        ...newStep,
        order_number: maxOrder + 1
      });
      toast.success('Step added successfully');
      setShowAddModal(false);
      setNewStep({ name: '', description: '', order_number: 1, requires_documents: false, is_active: true });
      fetchSteps();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add step');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateStep = async (stepId, updates) => {
    setSaving(true);
    try {
      await api.put(`/admin/company-approval/steps/${stepId}`, updates);
      toast.success('Step updated');
      setEditingStep(null);
      fetchSteps();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update step');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteStep = async (stepId) => {
    if (!window.confirm('Are you sure you want to delete this step? All associated questions will also be deleted.')) {
      return;
    }

    try {
      await api.delete(`/admin/company-approval/steps/${stepId}`);
      toast.success('Step deleted');
      fetchSteps();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete step');
    }
  };

  const handleMoveStep = async (index, direction) => {
    const newSteps = [...steps];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex < 0 || newIndex >= steps.length) return;
    
    // Swap order numbers
    const temp = newSteps[index].order_number;
    newSteps[index].order_number = newSteps[newIndex].order_number;
    newSteps[newIndex].order_number = temp;
    
    // Swap positions
    [newSteps[index], newSteps[newIndex]] = [newSteps[newIndex], newSteps[index]];
    
    setSteps(newSteps);
    
    try {
      // Update each step's order
      for (let i = 0; i < newSteps.length; i++) {
        await api.put(`/admin/company-approval/steps/${newSteps[i].id}`, { order_number: i + 1 });
      }
      fetchSteps();
    } catch (error) {
      toast.error('Failed to reorder steps');
      fetchSteps();
    }
  };

  if (loading) {
    return (
      <Sidebar>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-300" style={{ borderTopColor: BRAND_BLUE }}></div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="p-8 bg-gray-50/50 min-h-screen">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Building2 className="w-6 h-6 text-blue-600" />
              </div>
              <h1 className="text-2xl font-semibold text-gray-900">Company Approval Steps</h1>
            </div>
            <p className="text-gray-500 text-sm">Define the verification steps new companies must complete to get approved.</p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/admin/company-approval-questions"
              className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition font-medium text-sm"
            >
              <ListChecks className="w-4 h-4" />
              Manage Questions
            </Link>
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-5 py-2.5 text-white rounded-xl transition font-medium text-sm"
              style={{ backgroundColor: BRAND_BLUE }}
              data-testid="add-step-btn"
            >
              <Plus className="w-4 h-4" />
              Add Step
            </button>
          </div>
        </div>

        {/* Steps List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          {steps.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Approval Steps Yet</h3>
              <p className="text-gray-500 mb-6">Create steps that companies must complete to get verified.</p>
              <button
                onClick={() => setShowAddModal(true)}
                className="inline-flex items-center gap-2 px-5 py-2.5 text-white rounded-xl transition font-medium text-sm"
                style={{ backgroundColor: BRAND_BLUE }}
              >
                <Plus className="w-4 h-4" />
                Create First Step
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {steps.map((step, index) => (
                <div key={step.id} className="p-5 flex items-center gap-4" data-testid={`step-${step.id}`}>
                  {/* Drag Handle & Order */}
                  <div className="flex items-center gap-2">
                    <GripVertical className="w-5 h-5 text-gray-300" />
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold text-sm">
                      {index + 1}
                    </div>
                  </div>

                  {/* Step Info */}
                  <div className="flex-1 min-w-0">
                    {editingStep === step.id ? (
                      <div className="space-y-3">
                        <input
                          type="text"
                          defaultValue={step.name}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          id={`edit-name-${step.id}`}
                        />
                        <textarea
                          defaultValue={step.description || ''}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          rows={2}
                          placeholder="Description (optional)"
                          id={`edit-desc-${step.id}`}
                        />
                        <div className="flex items-center gap-4">
                          <label className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              defaultChecked={step.requires_documents}
                              id={`edit-docs-${step.id}`}
                              className="rounded border-gray-300"
                            />
                            Requires Document Uploads
                          </label>
                          <label className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              defaultChecked={step.is_active}
                              id={`edit-active-${step.id}`}
                              className="rounded border-gray-300"
                            />
                            Active
                          </label>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              const name = document.getElementById(`edit-name-${step.id}`).value;
                              const description = document.getElementById(`edit-desc-${step.id}`).value;
                              const requires_documents = document.getElementById(`edit-docs-${step.id}`).checked;
                              const is_active = document.getElementById(`edit-active-${step.id}`).checked;
                              handleUpdateStep(step.id, { name, description, requires_documents, is_active });
                            }}
                            disabled={saving}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setEditingStep(null)}
                            className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm hover:bg-gray-200"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-gray-900">{step.name}</h3>
                          {!step.is_active && (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full">Inactive</span>
                          )}
                          {step.requires_documents && (
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-600 text-xs rounded-full">Requires Docs</span>
                          )}
                        </div>
                        {step.description && (
                          <p className="text-sm text-gray-500 mt-1">{step.description}</p>
                        )}
                        <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                          <span>{step.question_count || 0} questions</span>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Actions */}
                  {editingStep !== step.id && (
                    <div className="flex items-center gap-2">
                      {/* Move Buttons */}
                      <button
                        onClick={() => handleMoveStep(index, 'up')}
                        disabled={index === 0}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-30"
                      >
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleMoveStep(index, 'down')}
                        disabled={index === steps.length - 1}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-30"
                      >
                        <ChevronDown className="w-4 h-4" />
                      </button>
                      
                      {/* Edit */}
                      <button
                        onClick={() => setEditingStep(step.id)}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      
                      {/* Delete */}
                      <button
                        onClick={() => handleDeleteStep(step.id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info Box */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-100 rounded-xl">
          <h4 className="font-medium text-blue-900 mb-2">How Company Approval Works</h4>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• When a new company registers, they must verify their email first</li>
            <li>• After email verification, they'll see these approval steps to complete</li>
            <li>• Each step can have multiple questions that the company admin must answer</li>
            <li>• Main admin reviews submissions and approves/denies each step</li>
            <li>• Once all steps are approved, the company becomes fully verified</li>
          </ul>
        </div>
      </div>

      {/* Add Step Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Add Approval Step</h2>
                <button 
                  onClick={() => setShowAddModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Step Name *</label>
                <input
                  type="text"
                  value={newStep.name}
                  onChange={(e) => setNewStep(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Business Information"
                  data-testid="new-step-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={newStep.description}
                  onChange={(e) => setNewStep(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Brief description of what this step covers..."
                  data-testid="new-step-description"
                />
              </div>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={newStep.requires_documents}
                    onChange={(e) => setNewStep(prev => ({ ...prev, requires_documents: e.target.checked }))}
                    className="rounded border-gray-300"
                  />
                  Requires Document Uploads
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={newStep.is_active}
                    onChange={(e) => setNewStep(prev => ({ ...prev, is_active: e.target.checked }))}
                    className="rounded border-gray-300"
                  />
                  Active
                </label>
              </div>
            </div>
            <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-5 py-2.5 text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 font-medium text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleAddStep}
                disabled={saving}
                className="px-5 py-2.5 text-white rounded-xl font-medium text-sm disabled:opacity-50"
                style={{ backgroundColor: BRAND_BLUE }}
                data-testid="save-step-btn"
              >
                {saving ? 'Saving...' : 'Add Step'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Sidebar>
  );
};

export default AdminCompanyApprovalSteps;
